class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.scrollLocked = false;
    this._unlockTimeout = null;
    this._touchStartY = 0;

    // iOS Safari: prevent body scroll and pull-to-refresh while drawer is open
    this._onTouchStart = (e) => {
      this._touchStartY = e.touches[0].clientY;
    };
    this._onTouchMove = (e) => {
      // Walk up from touch target to find a scrollable container inside the drawer
      let scrollable = null;
      let el = e.target;
      while (el && el !== this) {
        if (el.scrollHeight > el.clientHeight) {
          const ov = getComputedStyle(el).overflowY;
          if (ov === 'auto' || ov === 'scroll') {
            scrollable = el;
            break;
          }
        }
        el = el.parentElement;
      }
      if (!scrollable) {
        e.preventDefault();
        return;
      }
      // Prevent overscroll at scroll boundaries to block pull-to-refresh
      const deltaY = e.touches[0].clientY - this._touchStartY;
      const atTop = scrollable.scrollTop <= 0;
      const atBottom = scrollable.scrollTop + scrollable.clientHeight >= scrollable.scrollHeight - 1;
      if ((atTop && deltaY > 0) || (atBottom && deltaY < 0)) {
        e.preventDefault();
      }
    };

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    // Cancel any pending unlock from a previous close
    if (this._unlockTimeout) {
      clearTimeout(this._unlockTimeout);
      this._unlockTimeout = null;
    }
    this.scrollLocked = true;
    document.body.classList.add('overflow-hidden');
    this.addEventListener('touchstart', this._onTouchStart, { passive: true });
    this.addEventListener('touchmove', this._onTouchMove, { passive: false });
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    if (this.scrollLocked) {
      // Unlock body after the CSS close transition completes.
      // Using transitionend instead of setTimeout to avoid the overlay
      // flickering when the timer fires before the transition finishes.
      const unlockBody = () => {
        this.scrollLocked = false;
        document.body.classList.remove('overflow-hidden');
        this.removeEventListener('touchstart', this._onTouchStart);
        this.removeEventListener('touchmove', this._onTouchMove);
      };

      const inner = this.querySelector('.drawer__inner');
      if (inner) {
        const handler = (e) => {
          if (e.propertyName === 'transform') {
            inner.removeEventListener('transitionend', handler);
            if (this._unlockTimeout) {
              clearTimeout(this._unlockTimeout);
              this._unlockTimeout = null;
            }
            unlockBody();
          }
        };
        inner.addEventListener('transitionend', handler);
      }
      // Safety fallback: if transitionend never fires (e.g. transition
      // is cancelled or element is removed), unlock after 300ms.
      this._unlockTimeout = setTimeout(() => {
        this._unlockTimeout = null;
        unlockBody();
      }, 300);
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
